import i18next from 'i18next';

function toolTranslations(
  id: string,
  label: string,
  options?: { reasoning?: string; output?: string; noop?: string },
): Record<string, string> {
  return {
    [id]: label,
    [`${id}Reasoning`]: options?.reasoning ?? label,
    [`${id}Output`]: options?.output ?? `Completed: ${label}`,
    [`${id}Noop`]: options?.noop ?? `No results: ${label}`,
    [`${id}Error`]: `Error: ${label}`,
  };
}

/**
 * Registers the metric package i18n resource bundles.
 */
export default function initI18n(): void {
  i18next.addResourceBundle('en', 'metric', {
    customUnitConflict: 'A custom unit with this abbreviation already exists for this measure',
    customUnitSystemProtected: 'System units cannot be changed',
    customMetricInvalidExpression: 'Invalid calculation expression',
    customMetricDependencyNotFound: 'Dependency metric was not found',
  });
  i18next.addResourceBundle('en', 'ai-tool', {
    ...toolTranslations('findMetrics', 'Find metrics', {
      reasoning: 'Finding metrics',
      output: 'Found {{count}} metrics',
      noop: 'No metrics found',
    }),
    ...toolTranslations('findUserMetrics', 'Find user metrics', {
      reasoning: 'Finding tracked metrics',
      output: 'Found {{count}} tracked metrics',
      noop: 'No tracked metrics found',
    }),
    ...toolTranslations('findMetricGroups', 'Find metric groups', {
      reasoning: 'Finding metric groups',
      output: 'Found {{count}} metric groups',
      noop: 'No metric groups found',
    }),
    ...toolTranslations('findUserMetricGroups', 'Find user metric groups', {
      reasoning: 'Finding tracked metric groups',
      output: 'Found {{count}} tracked metric groups',
      noop: 'No tracked metric groups found',
    }),
    ...toolTranslations('findUnits', 'Find units', {
      reasoning: 'Finding units',
      output: 'Found {{count}} units',
      noop: 'No units found',
    }),
    ...toolTranslations('readMetric', 'Read metric', {
      reasoning: 'Reading metric',
      output: 'Read metric',
    }),
    ...toolTranslations('readUserMetric', 'Read user metric', {
      reasoning: 'Reading tracked metric',
      output: 'Read user metric',
    }),
    ...toolTranslations('readMetricGroup', 'Read metric group', {
      reasoning: 'Reading metric group',
      output: 'Read metric group',
    }),
    ...toolTranslations('readUserMetricGroup', 'Read user metric group', {
      reasoning: 'Reading tracked metric group',
      output: 'Read user metric group',
    }),
    ...toolTranslations('readUnit', 'Read unit', {
      reasoning: 'Reading unit',
      output: 'Read unit',
    }),
    ...toolTranslations('addUserMetric', 'Add user metric', {
      reasoning: 'Adding user metric',
      output: 'Added {{count}} metrics',
      noop: 'Metric already tracked',
    }),
    ...toolTranslations('addUserMetricGroup', 'Add user metric group', {
      reasoning: 'Adding metric group',
      output: 'Added metric group',
    }),
    ...toolTranslations('removeUserMetricGroup', 'Remove user metric group', {
      reasoning: 'Removing metric group',
      output: 'Stopped tracking metric group',
    }),
    ...toolTranslations('recordMetricValue', 'Record metric value', {
      reasoning: 'Recording metric value',
      output: 'Recorded metric value',
    }),
    ...toolTranslations('findMetricValues', 'Find metric values', {
      reasoning: 'Finding metric values',
      output: 'Found {{count}} values',
      noop: 'No metric values found',
    }),
    ...toolTranslations('updateMetricGoal', 'Update metric goal', {
      reasoning: 'Updating metric goal',
      output: 'Updated metric goal',
    }),
    ...toolTranslations('createMetric', 'Create metric', {
      reasoning: 'Creating metric',
      output: 'Created metric',
    }),
    ...toolTranslations('updateMetric', 'Update metric', {
      reasoning: 'Updating metric',
      output: 'Updated metric',
    }),
    ...toolTranslations('trashMetric', 'Trash metric', {
      reasoning: 'Trashing metric',
      output: 'Trashed metric',
    }),
    ...toolTranslations('updateUserMetric', 'Update user metric', {
      reasoning: 'Updating tracked metric',
      output: 'Updated user metric',
    }),
    ...toolTranslations('trashUserMetric', 'Trash user metric', {
      reasoning: 'Stopping metric tracking',
      output: 'Stopped tracking metric',
    }),
    ...toolTranslations('createMetricGroup', 'Create metric group', {
      reasoning: 'Creating metric group',
      output: 'Created metric group',
    }),
    ...toolTranslations('updateMetricGroup', 'Update metric group', {
      reasoning: 'Updating metric group',
      output: 'Updated metric group',
    }),
    ...toolTranslations('trashMetricGroup', 'Trash metric group', {
      reasoning: 'Trashing metric group',
      output: 'Trashed metric group',
    }),
    ...toolTranslations('createUnit', 'Create unit', {
      reasoning: 'Creating unit',
      output: 'Created unit',
    }),
    ...toolTranslations('updateUnit', 'Update unit', {
      reasoning: 'Updating unit',
      output: 'Updated unit',
    }),
    ...toolTranslations('trashUnit', 'Trash unit', {
      reasoning: 'Trashing unit',
      output: 'Trashed unit',
    }),
  });
}
